import { useCallback } from 'react';
import { __url } from '@99designs/i18n';
import { BRIEF_PAGE_NAME, sendAddToCartTrackingEvent } from '@99designs/tracking';
import { FulfilmentDataType, UpsertToCartRequest, useCart } from '../../lib/hooks';
import { getWorkIdFromDynamicFields } from './getWorkIdFromDynamicFields';
import type { ClaimBriefMutation } from '../../graph/ClaimBrief.generated';

export const useAddBriefToCart = (
    locale: string,
    accessToken: string,
    shopperId: string,
    briefId: string
) => {
    const { upsertToCart } = useCart(locale, { accessToken, shopperId });

    return useCallback(
        async (data: ClaimBriefMutation) => {
            const product = data.claimStructuredBrief.product;
            if (!product)
                throw new Error('Product is not present in claim structured brief response');

            const cartConfig = product.cimpressProductConfig;
            if (!cartConfig)
                throw new Error('Cart config is not present in claim structured brief response');

            const cartProduct: UpsertToCartRequest['product'] = {
                productKey: cartConfig.cimpressProductKey,
                productVersion: cartConfig.cimpressProductVersion,
                productName: product.title,
                fulfilmentData: fulfilmentDataForBifrostRequest(data.claimStructuredBrief),
            };

            await upsertToCart({
                product: cartProduct,
                correlationId: briefId,
                workId: getWorkIdFromDynamicFields(data.claimStructuredBrief.dynamicFields),
            });

            sendAddToCartTrackingEvent({
                pageName: BRIEF_PAGE_NAME,
                pageStage: 'Design',
                pageSection: 'Design Services',
                product_id: cartConfig.cimpressProductKey, // should be mpvId
                name: product.title,
                core_product_id: cartConfig.cimpressProductKey,
                price: Math.round(product.pricing.amount.amountInCents / 100),
                currency: product.pricing.amount.currency,
            });
        },
        [upsertToCart, briefId]
    );
};

const NN_FULFILLER_ID = 'wpjpx38925';

function fulfilmentDataForBifrostRequest(
    brief: ClaimBriefMutation['claimStructuredBrief']
): FulfilmentDataType {
    const product = brief.product;
    if (!product) throw new Error('Product is not present in claim structured brief response');

    const siteKey = brief.siteKey;
    if (!siteKey) throw new Error('Site key is not present in claim structured brief response');

    return {
        productBriefID: brief.id,
        siteKey: siteKey, // Used in abacus to determine the site to get a currency + exchange rate to build up payment metadata for the request
        orderKey: brief.briefHash, // Abacus sends this to create because the Fulfil RPC uses this to validate whether the brief was changed during the payment cycle
        postPaymentUrl: __url('/graphic-design/dashboard'), // NOTE: This URL has only been configured for en-US, en-AU and fr-FR for now
        fulfillerId: NN_FULFILLER_ID, // NOTE: This is the MCP supplier ID. The SNS subscription filter uses this to filter which order events we respond to
        productKey: product.key, //Note: abacus uses this to look up the designer payout value
        fulfilmentStrategy: product.fulfilmentStrategy,
    };
}
